import { AddressType, CodeStub, DecryptedAddress, DecryptedPatient, DecryptedPropertyStub, Gender, PersonNameUse, TelecomType } from '@icure/cardinal-sdk'
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/nl'
import { getAgeDescription } from './date'
import { DATE_FORMAT_TO_DISPLAY, PATIENT_ID_CARD_IDENTIFIER } from '../constants'
import { anyDateToDate } from '@icure/form/utils/dates'

export interface PatientFormated {
  id?: string
  firstName?: string
  lastName?: string
  dateOfBirth?: number
  userNameOneString?: string
  emailAddress?: string
  phoneNumber?: string
  birthSex?: Gender | undefined
  language?: string
  city?: string
  street?: string
  houseNumber?: string
  postalCode?: string
  userHomeAddressOneString?: string
  userDateOfBirthOneString?: string
  tags?: CodeStub[]
  age?: string
  delegatesIds?: string[]
  picture?: Int8Array
  userDateOfBirthPlusAge?: string
  ssin?: string
  properties?: DecryptedPropertyStub[]
  idCardNumber?: string
  responsible?: string
}

export const splitAddressGeneric = (address?: string): { nonNumericPart: string; numericPart: string } => {
  if (!address) {
    return { nonNumericPart: '', numericPart: '' }
  }
  const match = address.toString().match(/^(\d+)\s*[,\s]\s*(.+)$|^(.+?)\s*[,\s]\s*(\d+.*)$/)

  if (match) {
    return match[1] // If the first part is a number (postal code)
      ? { nonNumericPart: match[2]?.trim() ?? '', numericPart: match[1]?.trim() ?? '' }
      : { nonNumericPart: match[3]?.trim() ?? '', numericPart: match[4]?.trim() ?? '' }
  }

  if (address.toString().match(/^\d+$/)) {
    return { nonNumericPart: '', numericPart: address.toString() }
  } else {
    return { nonNumericPart: address.toString(), numericPart: '' }
  }
}
export const getPatientBirthDateFromImport = (date: string) => {
  // based on the DATE_FORMAT
  const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(\d{4})$/
  return dateRegex.test(date) ? date : null
}
export const getTelecomBySystem = (addresses: DecryptedAddress[], telecomSystem: TelecomType) => {
  const expectedAddress = addresses?.find(({ telecoms }) => telecoms.some(({ telecomType }) => telecomType === telecomSystem))
  const expectedTelecom = expectedAddress?.telecoms.find(({ telecomType }) => telecomType === telecomSystem)
  return expectedTelecom?.telecomNumber
}
export const getPatientDataFormated = (
  patient: DecryptedPatient | undefined,
  t: (
    key: string,
    ctxt?: {
      [key: string]: string
    },
  ) => string,
  languageOfTheApp: string,
): PatientFormated => {
  if (!patient) return {}

  const { id, names, firstName, lastName, dateOfBirth, addresses, birthSex, languages, tags, securityMetadata, picture, ssin, properties, responsible } = patient
  // get username
  const officialUserNameObj = names.find((nameElement) => nameElement.use === PersonNameUse.Official)
  const userFirstName = officialUserNameObj && officialUserNameObj?.firstNames?.length !== 0 ? officialUserNameObj?.firstNames?.join(' ') : firstName
  const userLastName = officialUserNameObj?.lastName ?? lastName
  const userName = userFirstName && userLastName ? userFirstName.concat(' ', userLastName) : (userFirstName ?? userLastName)

  // get user home address
  const homeAddressObj = addresses?.find(({ addressType }) => addressType === AddressType.Home)
  const homeAddressString = [homeAddressObj?.street, homeAddressObj?.houseNumber, homeAddressObj?.postalCode, homeAddressObj?.city, homeAddressObj?.country]
    .filter((el) => !!el)
    .join(', ')

  // get user birthday
  const parsedDate = dateOfBirth ? anyDateToDate(dateOfBirth) : undefined
  const userDateOfBirth = parsedDate ? dayjs(parsedDate).format(DATE_FORMAT_TO_DISPLAY) : undefined
  const userDateOfBirthPlusAge = () => {
    if (!dateOfBirth) return undefined
    const date = parsedDate ? dayjs(parsedDate).locale(languageOfTheApp).format('DD MMMM YYYY') : undefined
    return `${t('patientDataFormated.dateOfBirth')} ${date} (${getAgeDescription(dateOfBirth, languageOfTheApp)})`
  }

  // get the list of IDs of doctors with whom a patient is being shared using the iCure backend
  const delegatesIds: string[] | undefined = securityMetadata?.secureDelegations
    ? Object.values(securityMetadata.secureDelegations)
        .map((el) => (el.delegate !== el.delegator ? el.delegate : undefined))
        .filter((x): x is string => !!x)
    : undefined

  const idCardNumber = properties?.find((prop) => prop.id === PATIENT_ID_CARD_IDENTIFIER)?.typedValue?.stringValue

  return {
    id,
    firstName: userFirstName,
    lastName: userLastName,
    dateOfBirth,
    birthSex,
    tags,
    language: languages[0],
    emailAddress: getTelecomBySystem(addresses, TelecomType.Email),
    phoneNumber: getTelecomBySystem(addresses, TelecomType.Mobile),
    userNameOneString: userName,
    city: homeAddressObj?.city,
    street: homeAddressObj?.street,
    houseNumber: homeAddressObj?.houseNumber,
    postalCode: homeAddressObj?.postalCode,
    userHomeAddressOneString: homeAddressString.length !== 0 ? homeAddressString : undefined,
    userDateOfBirthOneString: userDateOfBirth,
    age: getAgeDescription(dateOfBirth, languageOfTheApp),
    delegatesIds,
    picture,
    userDateOfBirthPlusAge: userDateOfBirthPlusAge(),
    ssin,
    properties,
    idCardNumber,
    responsible,
  }
}
